import { useEffect, useState } from "react";
import { useSearch } from "wouter";
import {
  useConversationDetail,
  useConversations,
  type ConversationRow,
  type MessageRow,
} from "../lib/tenant-api";
import { formatDateTime } from "../lib/format";
import {
  EmptyState,
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

export default function Conversations() {
  const [selected, setSelected] = useState<ConversationRow | null>(null);
  const list = useConversations();
  const search = useSearch();

  // Deep-link support: `/dashboard/conversations?id=cv_xxx` auto-opens the
  // transcript drawer for that row. Used by ops emails (handoff alerts, lead
  // notifications, booking confirmations) so clicking the link in your inbox
  // takes you straight to the conversation, not the empty list page.
  useEffect(() => {
    const targetId = new URLSearchParams(search).get("id");
    if (!targetId || !list.data) return;
    const match = list.data.find((c) => c.id === targetId);
    if (match) setSelected(match);
  }, [search, list.data]);

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Conversations</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every voice call, web chat, and WhatsApp thread your AI receptionist handled.
        </p>
      </div>

      {list.isLoading && <LoadingRow />}
      {list.error && <ErrorBanner message={(list.error as Error).message} />}
      {list.data && list.data.length === 0 && (
        <EmptyState
          icon="💬"
          title="No conversations yet"
          hint="They'll appear here as soon as the receptionist takes its first call or chat."
        />
      )}
      {list.data && list.data.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left font-medium px-4 py-2">Caller</th>
                <th className="text-left font-medium px-4 py-2">Channel</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-left font-medium px-4 py-2">Started</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="px-4 py-3 text-slate-900">
                    {c.caller_name || c.caller_phone || c.caller_email || (
                      <span className="text-slate-400">(anonymous)</span>
                    )}
                    {c.summary && (
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                        {c.summary}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ChannelBadge channel={c.channel} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {formatDateTime(c.started_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <TranscriptDrawer
          conversation={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function TranscriptDrawer({
  conversation,
  onClose,
}: {
  conversation: ConversationRow;
  onClose: () => void;
}) {
  const detail = useConversationDetail(conversation.id);
  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex justify-end z-20"
      onClick={onClose}
    >
      <div
        className="w-full md:w-[640px] bg-white h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-slate-200 px-5 py-4 flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-slate-900">
              {conversation.caller_name ||
                conversation.caller_phone ||
                conversation.caller_email ||
                "Anonymous caller"}
            </h2>
            <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
              <ChannelBadge channel={conversation.channel} />
              <StatusBadge status={conversation.status} />
              <span>{formatDateTime(conversation.started_at)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
          >
            ×
          </button>
        </header>
        <div className="px-5 py-4">
          {detail.isLoading && <LoadingRow label="Loading transcript…" />}
          {detail.error && (
            <ErrorBanner message={(detail.error as Error).message} />
          )}
          {detail.data && (
            <Transcript messages={detail.data.messages} />
          )}
        </div>
      </div>
    </div>
  );
}

function Transcript({ messages }: { messages: MessageRow[] }) {
  if (messages.length === 0) {
    return (
      <p className="text-sm text-slate-400 italic">
        (Conversation has no messages yet.)
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <MessageBubble key={m.id} m={m} />
      ))}
    </div>
  );
}

function MessageBubble({ m }: { m: MessageRow }) {
  if (m.role === "tool") {
    return (
      <div className="text-xs text-slate-500 italic">
        ✓ tool: {m.tool_name ?? "(unnamed)"}
        {m.tool_input && (
          <code className="ml-1 text-slate-400">
            {truncate(m.tool_input, 80)}
          </code>
        )}
      </div>
    );
  }
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
          isUser
            ? "bg-brand-600 text-white"
            : "bg-slate-100 text-slate-900 border border-slate-200"
        }`}
      >
        {m.content}
      </div>
    </div>
  );
}

function ChannelBadge({ channel }: { channel: string }) {
  const map: Record<string, { label: string; color: string }> = {
    web: { label: "Web", color: "bg-blue-100 text-blue-800" },
    voice: { label: "Voice", color: "bg-purple-100 text-purple-800" },
    whatsapp: { label: "WhatsApp", color: "bg-emerald-100 text-emerald-800" },
    sms: { label: "SMS", color: "bg-amber-100 text-amber-800" },
  };
  const m = map[channel] ?? { label: channel, color: "bg-slate-100 text-slate-700" };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded ${m.color}`}>
      {m.label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === "active" || status === "resolved";
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        ok
          ? "bg-emerald-100 text-emerald-700"
          : status === "handoff"
            ? "bg-amber-100 text-amber-800"
            : "bg-slate-100 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
